import { ArrowLeft, Download, Shield, RefreshCw, Smartphone, Lock, CheckCircle, Zap, Eye, Server, BadgeCheck, Sparkles, Bug, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';

const APK_DOWNLOAD_URL = 'https://customer-assets-agu9un31.emergentagent.net/job_trap-performance/artifacts/e9wl53u5_application-796b7792-8596-4f08-867b-77a03a2e9cbd.apk';
const APK_VERSION = '1.0.39';
const APK_DATE = '20 Fév 2026';

// Release notes: most recent first. `highlights` use keywords "new"|"fix"|"perf"
// to pick an appropriate icon. Keep items short (1 line each).
const RELEASE_NOTES = [
  {
    version: '1.0.39',
    date: '20 Fév 2026',
    highlights: [
      { type: 'fix', text: "Corrections de bugs et améliorations diverses" },
      { type: 'perf', text: "Optimisations de performance et de stabilité" },
    ],
  },
  {
    version: '1.0.38',
    date: '14 Mai 2026',
    highlights: [
      { type: 'fix', text: "Corrections de bugs et améliorations diverses" },
      { type: 'perf', text: "Optimisations de performance et de stabilité" },
    ],
  },
  {
    version: '1.0.34',
    date: '6 Mai 2026',
    highlights: [
      { type: 'fix', text: "Correction du crash au lancement de l'application" },
      { type: 'fix', text: "Correction d'un bug au Paywall" },
      { type: 'perf', text: "Amélioration de la fluidité de l'application" },
    ],
  },
  {
    version: '1.0.24',
    date: '4 Mai 2026',
    highlights: [
      { type: 'fix', text: "Correction des bugs au démarrage de l'application" },
      { type: 'perf', text: "Démarrage plus rapide et plus fiable" },
    ],
  },
  {
    version: '1.0.23',
    date: '4 Mai 2026',
    highlights: [
      { type: 'fix', text: "Correctifs de stabilité et petites améliorations" },
    ],
  },
  {
    version: '1.0.22',
    date: '3 Mai 2026',
    highlights: [
      { type: 'fix', text: "Correction du score affiché en /100 sur les sessions DTL (maintenant /300)" },
      { type: 'perf', text: "Chargement des sessions et de la carte des lieux plus rapide" },
      { type: 'new', text: "Préservation de la discipline DTL lors de l'édition d'une session" },
    ],
  },
  {
    version: '1.0.19',
    date: '1 Mai 2026',
    highlights: [
      { type: 'new', text: "Swipe gauche pour éditer, droite pour supprimer une session" },
      { type: 'new', text: "2 nouveaux stands partenaires : Silly-Tillard (60) et Vendôme (41)" },
      { type: 'fix', text: "Mise à jour automatique de l'app via le service worker" },
      { type: 'fix', text: "Correction de l'affichage de la page d'accueil après installation PWA" },
    ],
  },
  {
    version: '1.0.18',
    date: '28 Avril 2026',
    highlights: [
      { type: 'new', text: "Paiements Stripe sécurisés en mode LIVE (abonnement Pro)" },
      { type: 'fix', text: "Suppression de lieu directement depuis le formulaire" },
      { type: 'fix', text: "Reset rapide du mode live-scoring" },
    ],
  },
];

const HIGHLIGHT_ICONS = { new: Sparkles, fix: Bug, perf: Rocket };
const HIGHLIGHT_COLORS = {
  new: 'text-[#FFC107] bg-[#FFC107]/10',
  fix: 'text-[#FF6B35] bg-[#FF6B35]/10',
  perf: 'text-green-400 bg-green-400/10',
};

const steps = [
  {
    num: 1,
    title: 'Téléchargez le fichier APK',
    desc: 'Cliquez sur le bouton ci-dessous pour télécharger la dernière version de ClayPulse.',
    icon: Download,
  },
  {
    num: 2,
    title: 'Autorisez les sources inconnues',
    desc: 'Allez dans Paramètres > Sécurité > Sources inconnues et activez l\'option pour votre navigateur. Sur certains appareils : Paramètres > Applications > Autoriser l\'installation d\'applis inconnues.',
    icon: Shield,
  },
  {
    num: 3,
    title: 'Installez l\'application',
    desc: 'Ouvrez le fichier APK téléchargé depuis vos notifications ou votre gestionnaire de fichiers, puis appuyez sur "Installer".',
    icon: Smartphone,
  },
  {
    num: 4,
    title: 'Mises à jour',
    desc: 'Pour mettre à jour, revenez sur cette page et téléchargez la dernière version. Installez-la par-dessus la version existante, vos données seront conservées.',
    icon: RefreshCw,
  },
];

const trustSignals = [
  { icon: Lock, text: 'Téléchargement 100% sécurisé' },
  { icon: CheckCircle, text: 'Gratuit — aucun paiement requis' },
  { icon: Zap, text: 'Installation rapide (< 2 min)' },
  { icon: Eye, text: 'Aucune donnée collectée à l\'installation' },
  { icon: Server, text: 'Hébergé sur des serveurs sécurisés' },
  { icon: BadgeCheck, text: 'APK signé et vérifié par ClayPulse' },
];

export default function AndroidDownload() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-3xl mx-auto px-6 py-20">
        <Link
          to="/"
          data-testid="android-back-link"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-10 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <img
            src="/assets/img/logo-claypulse.jpg"
            alt="ClayPulse"
            className="w-16 h-16 rounded-2xl object-cover"
          />
          <div>
            <h1 className="font-['Poppins'] font-bold text-2xl sm:text-3xl">
              ClayPulse pour Android
            </h1>
            <p className="text-[#A0A0A0] text-sm mt-1">Téléchargement direct de l'APK — gratuit et sécurisé</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8"
        >
          {trustSignals.map((signal, i) => {
            const Icon = signal.icon;
            return (
              <div key={i} className="flex items-center gap-2.5 bg-[#1A1A1A] rounded-xl border border-white/5 px-4 py-3">
                <Icon size={16} className="text-[#FF6B35] shrink-0" />
                <span className="text-[#A0A0A0] text-xs leading-tight">{signal.text}</span>
              </div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-[#1A1A1A] rounded-2xl border border-white/5 p-6 sm:p-8 mb-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-white font-['Poppins'] font-semibold text-lg">Dernière version</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className="px-3 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-semibold font-['Poppins']">
                  v{APK_VERSION}
                </span>
                <span className="text-[#A0A0A0] text-xs">{APK_DATE}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle size={16} />
              <span className="text-xs font-medium">Vérifié et sécurisé</span>
            </div>
          </div>

          <a
            href={APK_DOWNLOAD_URL}
            data-testid="apk-download-button"
            className="pulse-cta w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold text-base hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all duration-300 hover:scale-[1.02]"
          >
            <Download size={20} />
            Télécharger gratuitement ({APK_VERSION})
          </a>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 text-xs text-[#A0A0A0]">
            <span>Nécessite Android 8.0+</span>
            <span className="hidden sm:inline">&bull;</span>
            <span className="flex items-center gap-1"><Lock size={12} /> Connexion chiffrée SSL</span>
            <span className="hidden sm:inline">&bull;</span>
            <span className="flex items-center gap-1"><Shield size={12} /> Sans virus ni malware</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-[#FF6B35]/5 to-[#FF8A5B]/5 border border-[#FF6B35]/10 rounded-xl p-4 mb-6 flex items-start gap-3"
        >
          <Shield size={20} className="text-[#FF6B35] shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-medium mb-1">Téléchargement 100% sûr et gratuit</p>
            <p className="text-[#A0A0A0] text-xs leading-relaxed">
              Ce fichier APK est signé numériquement par ClayPulse. Il est identique à la version soumise sur le Google Play Store.
              Aucune donnée personnelle n'est collectée lors du téléchargement. Votre vie privée est notre priorité.
            </p>
          </div>
        </motion.div>

        <ReleaseNotesSection />

        <h2 className="font-['Poppins'] font-bold text-xl sm:text-2xl mb-8">
          Comment installer l'APK
        </h2>

        <div className="space-y-4 mb-12">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
                data-testid={`install-step-${step.num}`}
                className="bg-[#1A1A1A] rounded-xl border border-white/5 p-5 flex gap-4 hover:border-[#FF6B35]/20 transition-colors duration-300"
              >
                <div className="shrink-0 w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <Icon size={20} className="text-[#FF6B35]" />
                </div>
                <div>
                  <h3 className="text-white font-['Poppins'] font-semibold text-sm sm:text-base mb-1">
                    <span className="text-[#FF6B35] mr-2">{step.num}.</span>
                    {step.title}
                  </h3>
                  <p className="text-[#A0A0A0] text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="bg-[#1A1A1A] rounded-2xl border border-white/5 p-6 sm:p-8 mb-8">
          <h3 className="text-white font-['Poppins'] font-semibold text-lg mb-4">Questions fréquentes</h3>
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-white font-medium mb-1">Pourquoi un APK et pas le Google Play Store ?</p>
              <p className="text-[#A0A0A0] leading-relaxed">
                L'application est en cours de validation sur le Google Play Store. En attendant, nous distribuons une version APK directe pour que vous puissiez en profiter dès maintenant. L'APK est strictement identique à la version Store.
              </p>
            </div>
            <div className="border-t border-white/5 pt-4">
              <p className="text-white font-medium mb-1">Le téléchargement est-il vraiment gratuit ?</p>
              <p className="text-[#A0A0A0] leading-relaxed">
                Oui, le téléchargement et l'installation sont entièrement gratuits. L'application propose ensuite un essai gratuit de 7 jours, sans carte bancaire requise.
              </p>
            </div>
            <div className="border-t border-white/5 pt-4">
              <p className="text-white font-medium mb-1">L'APK contient-il des virus ou publicités ?</p>
              <p className="text-[#A0A0A0] leading-relaxed">
                Non. L'APK est signé par notre certificat de développeur officiel. Il ne contient aucun virus, aucun malware et aucune publicité. Vous pouvez le vérifier avec n'importe quel antivirus.
              </p>
            </div>
            <div className="border-t border-white/5 pt-4">
              <p className="text-white font-medium mb-1">Mes données sont-elles en sécurité ?</p>
              <p className="text-[#A0A0A0] leading-relaxed">
                Absolument. Toutes vos données sont chiffrées en transit (SSL/TLS) et au repos. Nous sommes conformes au RGPD. Nous ne vendons ni ne partageons jamais vos données personnelles.
              </p>
            </div>
            <div className="border-t border-white/5 pt-4">
              <p className="text-white font-medium mb-1">Comment mettre à jour ?</p>
              <p className="text-[#A0A0A0] leading-relaxed">
                Revenez sur cette page pour télécharger la dernière version. Installez-la par-dessus l'ancienne : vos données et votre compte seront intégralement conservés.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mb-8 py-6 border-y border-white/5">
          {[
            { icon: Shield, label: 'Conforme RGPD' },
            { icon: Lock, label: 'Données chiffrées' },
            { icon: BadgeCheck, label: 'APK signé' },
            { icon: CheckCircle, label: '0 pub, 0 tracking' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center gap-2">
                <Icon size={16} className="text-[#FF6B35]" />
                <span className="text-[#A0A0A0] text-xs font-medium">{item.label}</span>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <p className="text-[#A0A0A0] text-sm">
            Un problème d'installation ?{' '}
            <a href="mailto:supportclaypulse@gmail.com" className="text-[#FF6B35] hover:underline">
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function ReleaseNotesSection() {
  const [expanded, setExpanded] = useState(false);
  const latest = RELEASE_NOTES[0];
  const older = RELEASE_NOTES.slice(1);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      className="mb-12"
      data-testid="release-notes-section"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-['Poppins'] font-bold text-xl sm:text-2xl text-white">Nouveautés</h2>
          <p className="text-[#A0A0A0] text-xs mt-1">Quoi de neuf dans cette mise à jour</p>
        </div>
        {older.length > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-[#FF6B35] hover:underline"
            data-testid="release-notes-toggle"
          >
            {expanded ? 'Masquer l\'historique' : `Voir l'historique (${older.length})`}
          </button>
        )}
      </div>

      {/* Latest version — highlighted card */}
      <div className="rounded-2xl bg-gradient-to-br from-[#FF6B35]/15 via-[#141414] to-[#FFC107]/10 border border-[#FF6B35]/30 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FF6B35] text-white text-[10px] font-bold tracking-wider uppercase">
            Nouveau
          </span>
          <span className="text-white font-['Poppins'] font-bold text-base">v{latest.version}</span>
          <span className="text-[#A0A0A0] text-xs">· {latest.date}</span>
        </div>
        <ul className="space-y-2.5">
          {latest.highlights.map((h, i) => {
            const Icon = HIGHLIGHT_ICONS[h.type] || Sparkles;
            const colors = HIGHLIGHT_COLORS[h.type] || HIGHLIGHT_COLORS.new;
            return (
              <li key={i} className="flex items-start gap-3 text-sm text-[#E0E0E0]">
                <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg ${colors}`}>
                  <Icon size={12} />
                </span>
                <span className="leading-relaxed pt-0.5">{h.text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Older versions — collapsible */}
      {expanded && older.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="release-notes-history">
          {older.map((rel) => (
            <div
              key={rel.version}
              className="rounded-xl bg-white/[0.02] border border-white/5 p-4"
            >
              <div className="flex items-center gap-2 mb-2.5">
                <span className="text-white font-['Poppins'] font-semibold text-sm">v{rel.version}</span>
                <span className="text-[#888] text-xs">· {rel.date}</span>
              </div>
              <ul className="space-y-1.5">
                {rel.highlights.map((h, i) => {
                  const Icon = HIGHLIGHT_ICONS[h.type] || Sparkles;
                  const colors = HIGHLIGHT_COLORS[h.type] || HIGHLIGHT_COLORS.new;
                  return (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#B8B8B8]">
                      <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-md ${colors}`}>
                        <Icon size={10} />
                      </span>
                      <span className="leading-relaxed pt-0.5">{h.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );
}
